import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import SelectComponent from "@via/components/select/select";
import Table from "@via/components/table/table.tsx";
import {
  fetchPipelineDetails,
  usePipelineRevisionHistory,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { formatPipelineProcessData } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { EMPTY_TABLE_MESSAGE } from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { ProcessDataInterface, SelectOption } from "@via/utils/interfaces.tsx";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

const columnHelper = createColumnHelper<ProcessDataInterface>();
const page = 0;
const pageSize = 10;

export default function EditModuleModal({
  data,
  onCancel,
  pipelineId,
  onChangeRevision,
}) {
  const [moduleData, setModuleData] = useState();
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [moduleId, setModuleId] = useState(pipelineId);
  const [revisionOptions, setRevisionOptions] = useState([]);
  const {
    data: pipelineRevisionHistoryData,
    isLoading: isPipelineRevisionHistoryLoading,
    isSuccess: isPipelineRevisionHistorySuccess,
  } = usePipelineRevisionHistory(pipelineId);

  useEffect(() => {
    if (data) {
      setModuleData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isPipelineRevisionHistorySuccess) {
      const options = pipelineRevisionHistoryData?.data?.map(rev => {
        return {
          label: rev?.version?.length
            ? `Version: ${rev?.version}`
            : "Version: 1.0.0",
          version: rev?.version.length ? rev?.version : "1.0.0",
          createdAt: rev?.createdAt,
          revComment: rev?.revComment?.length
            ? rev?.revComment
            : "Initial Version",
          value: rev?.id,
        };
      });
      let selected = options?.find(
        (rev: SelectOption) => rev?.value === Number(moduleId)
      );
      if (!selected) {
        selected = options[0];
      }
      setSelectedRevision(selected);

      setRevisionOptions(options);
    }
  }, [isPipelineRevisionHistorySuccess]);

  const handlePipelineRevisionChange = selected => {
    if (pipelineRevisionHistoryData) {
      const { data } = pipelineRevisionHistoryData;
      const retrieved = data?.find(d => d.id === selected.value);
      fetchPipelineDetails(retrieved?.id).then(data => {
        const parsed = formatPipelineProcessData(data?.pipeline);
        setModuleData(parsed);
      });
      setModuleId(selected.value);
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      cell: info => {
        return (
          <>
            <div id={`tooltip-${info.row.original.id}`}>{info.getValue()}</div>
            <Tooltip
              anchorSelect={`#tooltip-${info.row.original.id}`}
              content="Please click the 'Go to Module' link to edit or view the process."
              className="z-10"
            />
          </>
        );
      },
      header: () => <span>Process Name</span>,
    }),
    columnHelper.accessor("rev_id", {
      cell: info => info.getValue(),
      header: () => <span>Rev Id</span>,
    }),
    columnHelper.accessor("rev_comment", {
      cell: info => info.getValue(),
      header: () => <span>Rev Comment</span>,
    }),
    columnHelper.accessor("date_modified", {
      cell: info => {
        return (
          <span>
            {formatDateString(info.row.original.date_modified, "LLL")}
          </span>
        );
      },
      header: () => <span>Last Modified</span>,
    }),
  ] as Array<ColumnDef<ProcessDataInterface>>;

  return (
    <>
      {revisionOptions && revisionOptions?.length ? (
        <div className="mr-4 mt-4 flex w-full justify-end rounded-md border-gray-300 bg-white px-4 pt-1 text-gray-500 space-x-4">
          <div className="flex justify-center items-center py-2">
            <Link
              to={`/pipelines/${moduleId}`}
              className="via-link btn btn-primary truncate px-4"
              target="_blank">
              Go to Module
            </Link>
          </div>
          <div className="flex items-center">
            <SelectComponent
              name="pipelineRevision"
              options={revisionOptions}
              isLoading={isPipelineRevisionHistoryLoading}
              value={selectedRevision}
              enableClear={false}
              onChange={handlePipelineRevisionChange}
              formatOptionLabel={(option, { context }) => {
                return context === "menu" ? (
                  <div className="flex flex-col items-start">
                    <span>
                      {option?.version} on{" "}
                      {formatDateString(option?.createdAt ?? "", "LL")}
                    </span>
                    <span className="line-clamp-2 text-xs text-gray-400">
                      {option?.revComment}
                    </span>
                  </div>
                ) : (
                  option?.label
                );
              }}
            />
          </div>
        </div>
      ) : null}
      <form
        className="flex h-full flex-col divide-gray-200 bg-white sm:min-w-[480px] md:min-w-[900px]"
        onSubmit={e => {
          e.preventDefault();
          onChangeRevision(moduleId);
        }}>
        <div className="h-0 flex-1 overflow-y-auto">
          <div className="modal-header">
            <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
              Edit Module
            </h3>
          </div>
        </div>
        <div className="modal-body">
          <Table
            data={moduleData}
            columns={columns}
            page={page}
            pageSize={pageSize}
            emptyMessage={EMPTY_TABLE_MESSAGE.PROCESSES}
          />
        </div>
        <div className="modal-footer flex justify-end space-x-2">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => onCancel()}>
            Cancel
          </button>
          {pipelineId !== moduleId && (
            <button
              type="submit"
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Select Revision
            </button>
          )}
        </div>
      </form>
    </>
  );
}
